// BOARD-ORDER: a board's visible controls (the founder's "every reorder action is a visible
// hover icon"): the colour swatch picker behind the board's mark (header, Work home), the lead
// picker (an avatar, or "+ Lead" on hover), and the arrangement items of the board's "…" menu
// (Move up, Move down, Move to space, Colour, Lead) for the spaces tree and Work home. Every
// write is optimistic (board-writes.ts).
import {
  AvatarTile,
  BOARD_COLORS,
  type BoardColor,
  BoardMark,
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@companyos/ui";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowDownIcon,
  ArrowUpIcon,
  FolderInputIcon,
  PaletteIcon,
  UserIcon,
  UserPlusIcon,
} from "lucide-react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { boardsOf, orderedSpaces, stepTargets } from "./board-order";
import { useBoardWrites } from "./board-writes";
import { type SeatPeople, useSeatPeople } from "./model";
import { boardsQuery, spacesQuery } from "./work-queries";

type Board = Awaited<ReturnType<Api["client"]["boards"]["get"]>>;
type BoardRef = Pick<Board, "id" | "name" | "color">;

/** The swatches: "By name" (no colour) and the six tints, as a grid of the board's mark. */
function Swatches({
  board,
  onPick,
}: {
  board: BoardRef;
  onPick: (color: BoardColor | null) => void;
}) {
  const options = [{ color: null, label: "By name" }, ...BOARD_COLORS] as const;
  return (
    // biome-ignore lint/a11y/useSemanticElements: a group of toggle buttons, not a form field set.
    <div role="group" aria-label={`Colour of ${board.name}`} className="grid grid-cols-7 gap-1">
      {options.map((o) => {
        const on = (board.color ?? null) === o.color;
        return (
          <button
            key={o.color ?? "auto"}
            type="button"
            aria-label={o.label}
            aria-pressed={on}
            title={o.label}
            data-swatch={o.color ?? "auto"}
            onClick={() => onPick(o.color)}
            className={cn(
              "flex size-7 items-center justify-center rounded-md outline-none hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring",
              on && "ring-2 ring-primary",
            )}
          >
            <BoardMark name={board.name} color={o.color} size="sm" />
          </button>
        );
      })}
    </div>
  );
}

/**
 * The board's mark as a button: a popover of colour swatches. Read-only (`onPick` absent) it is
 * the plain mark.
 */
export function BoardColorButton({
  board,
  size = "sm",
  onPick,
  className,
}: {
  board: BoardRef;
  size?: "xs" | "sm" | "md";
  onPick?: ((color: BoardColor | null) => void) | undefined;
  className?: string;
}) {
  if (!onPick) return <BoardMark name={board.name} color={board.color} size={size} />;
  return (
    <Popover>
      <PopoverTrigger
        aria-label={`Colour of ${board.name}`}
        title="Change colour"
        data-slot="board-color"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "relative z-10 inline-flex shrink-0 rounded-sm outline-none hover:ring-2 hover:ring-border-strong focus-visible:outline-2 focus-visible:outline-ring",
          className,
        )}
      >
        <BoardMark name={board.name} color={board.color} size={size} />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <p className="mb-1.5 px-0.5 text-xs font-medium text-muted-foreground">Board colour</p>
        <Swatches board={board} onPick={onPick} />
      </PopoverContent>
    </Popover>
  );
}

/** The people a board may be led by: seats held by a person (no open seats, no DEs). */
const leaders = (people: SeatPeople) => people.all.filter((p) => !p.de && !p.closed && !p.open);

/**
 * The lead as a menu: the lead's tile and name, or "+ Lead" (on hover when `quiet`). Read-only
 * (`onPick` absent) it shows the lead only.
 */
export function LeadPicker({
  people,
  seatId,
  boardName,
  onPick,
  quiet = false,
  className,
}: {
  people: SeatPeople;
  seatId: string | null;
  boardName: string;
  onPick?: ((seatId: string | null) => void) | undefined;
  /** "+ Lead" shows on the row's hover only (Work home). */
  quiet?: boolean;
  className?: string;
}) {
  const lead = seatId ? people.of(seatId) : null;
  const face = lead ? (
    <>
      <AvatarTile size="sm" name={lead.name} kind={lead.de ? "agent" : "person"} />
      <span className="truncate">{lead.label}</span>
    </>
  ) : (
    <>
      <UserPlusIcon aria-hidden="true" className="size-4 shrink-0" />
      <span>Lead</span>
    </>
  );
  if (!onPick)
    return lead ? (
      <span className={cn("flex min-w-0 items-center gap-1.5", className)} title={lead.full}>
        {face}
      </span>
    ) : (
      <span className="text-faint">—</span>
    );
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        aria-label={lead ? `Lead of ${boardName}: ${lead.full}` : `Choose a lead for ${boardName}`}
        title={lead?.full ?? "Choose a lead"}
        data-slot="board-lead"
        onClick={(e) => e.stopPropagation()}
        className={cn(
          "relative z-10 flex h-7 min-w-0 max-w-full items-center gap-1.5 rounded-md px-1 text-sm outline-none hover:bg-muted focus-visible:outline-2 focus-visible:outline-ring",
          !lead && "text-xs text-faint hover:text-foreground",
          !lead &&
            quiet &&
            "opacity-0 group-hover/board-row:opacity-100 group-focus-within/board-row:opacity-100 focus-visible:opacity-100 pointer-coarse:opacity-100",
          className,
        )}
      >
        {face}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="min-w-56">
        <LeadItems people={people} seatId={seatId} onPick={onPick} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LeadItems({
  people,
  seatId,
  onPick,
}: {
  people: SeatPeople;
  seatId: string | null;
  onPick: (seatId: string | null) => void;
}) {
  const list = leaders(people);
  return (
    <DropdownMenuGroup>
      <DropdownMenuLabel>Lead</DropdownMenuLabel>
      <DropdownMenuRadioGroup
        value={seatId ?? ""}
        onValueChange={(v) => onPick(v ? String(v) : null)}
      >
        <DropdownMenuRadioItem value="" closeOnClick>
          <UserIcon aria-hidden="true" />
          No lead
        </DropdownMenuRadioItem>
        {list.map((p) => (
          <DropdownMenuRadioItem key={p.seatId} value={p.seatId} closeOnClick>
            <AvatarTile size="xs" name={p.name} />
            <span className="truncate">{p.name}</span>
            <span className="mr-5 ml-auto shrink-0 text-xs text-muted-foreground">{p.hint}</span>
          </DropdownMenuRadioItem>
        ))}
      </DropdownMenuRadioGroup>
    </DropdownMenuGroup>
  );
}

/**
 * The "…" menu's arrangement items for a board: Move up / Move down within its space, Move to
 * space (each other live shared space, at its end), Colour and Lead. Rendered inside the open
 * menu only, so its reads (cached lists, the seats) run when the menu opens.
 */
export function BoardArrangeItems({
  api,
  company,
  board,
}: {
  api: Api;
  company: CompanyAccess;
  board: Board;
}) {
  const co = company.id;
  const boards = useQuery(boardsQuery(api, co)).data?.data ?? [];
  const spaces = orderedSpaces(
    (useQuery(spacesQuery(api, co)).data?.data ?? []).filter(
      (s) => !s.archived_at && s.kind !== "personal",
    ),
  );
  const people = useSeatPeople(api, co);
  const writes = useBoardWrites(api, company);
  const live = boards.find((b) => b.id === board.id) ?? board;
  const ids = boardsOf(boards, live.space_id).map((b) => b.id);
  const step = stepTargets(ids, live.id);
  return (
    <>
      <DropdownMenuSeparator />
      <DropdownMenuItem
        disabled={step.up === undefined}
        onClick={() => step.up !== undefined && writes.move(live, live.space_id, step.up)}
      >
        <ArrowUpIcon aria-hidden="true" />
        Move up
      </DropdownMenuItem>
      <DropdownMenuItem
        disabled={step.down === undefined}
        onClick={() => step.down !== undefined && writes.move(live, live.space_id, step.down)}
      >
        <ArrowDownIcon aria-hidden="true" />
        Move down
      </DropdownMenuItem>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger disabled={spaces.length < 2}>
          <FolderInputIcon aria-hidden="true" />
          Move to space
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="min-w-44">
          {spaces
            .filter((s) => s.id !== live.space_id)
            .map((s) => (
              <DropdownMenuItem key={s.id} onClick={() => writes.move(live, s.id, null, s.name)}>
                {s.name}
              </DropdownMenuItem>
            ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <PaletteIcon aria-hidden="true" />
          Colour
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="min-w-40">
          <DropdownMenuRadioGroup
            value={live.color ?? ""}
            onValueChange={(v) => writes.color(live, v ? (v as BoardColor) : null)}
          >
            <DropdownMenuRadioItem value="" closeOnClick>
              <BoardMark name={live.name} size="xs" />
              By name
            </DropdownMenuRadioItem>
            {BOARD_COLORS.map((c) => (
              <DropdownMenuRadioItem key={c.color} value={c.color} closeOnClick>
                <BoardMark name={live.name} color={c.color} size="xs" />
                {c.label}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuSubContent>
      </DropdownMenuSub>
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <UserIcon aria-hidden="true" />
          Lead
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent className="min-w-56">
          <LeadItems
            people={people}
            seatId={live.lead_seat_id}
            onPick={(seat) => writes.lead(live, seat)}
          />
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    </>
  );
}
